import { useCallback, useRef, useState } from 'react'
import { supabase } from '@/lib/supabase'

/* ============================================================
   ImageUploader — PRD §8.1 CMS
   Drop-zone for image fields. Uploads to tenant-media bucket.
   Returns the public URL.
   ============================================================ */

interface ImageUploaderProps {
  tenantId: string
  section: string
  fieldKey: string
  currentUrl?: string
  onUpload: (url: string) => void
}

export default function ImageUploader({ tenantId, section, fieldKey, currentUrl, onUpload }: ImageUploaderProps) {
  const [uploading, setUploading] = useState(false)
  const [preview, setPreview] = useState<string | null>(currentUrl || null)
  const [dragOver, setDragOver] = useState(false)
  const [error, setError] = useState<string | null>(null)
  const inputRef = useRef<HTMLInputElement>(null)

  const handleFile = useCallback(
    async (file: File) => {
      if (!file.type.startsWith('image/')) {
        setError('Only image files are allowed')
        return
      }

      if (file.size > 5 * 1024 * 1024) {
        setError('File must be under 5MB')
        return
      }

      setError(null)
      setUploading(true)

      // Generate unique path: tenant-id/section/fieldKey-timestamp.ext
      const ext = file.name.split('.').pop() || 'jpg'
      const path = `${tenantId}/${section}/${fieldKey}-${Date.now()}.${ext}`

      try {
        const { error: uploadError } = await supabase.storage
          .from('tenant-media')
          .upload(path, file, {
            cacheControl: '3600',
            upsert: true,
          })

        if (uploadError) throw uploadError

        const { data } = supabase.storage.from('tenant-media').getPublicUrl(path)
        const publicUrl = data.publicUrl

        setPreview(publicUrl)
        onUpload(publicUrl)
      } catch (err: unknown) {
        setError(err instanceof Error ? err.message : 'Upload failed')
      } finally {
        setUploading(false)
      }
    },
    [tenantId, section, fieldKey, onUpload],
  )

  const handleDrop = useCallback(
    (e: React.DragEvent) => {
      e.preventDefault()
      setDragOver(false)
      const file = e.dataTransfer.files[0]
      if (file) handleFile(file)
    },
    [handleFile],
  )

  const handleChange = useCallback(
    (e: React.ChangeEvent<HTMLInputElement>) => {
      const file = e.target.files?.[0]
      if (file) handleFile(file)
    },
    [handleFile],
  )

  return (
    <div className="space-y-2">
      {/* Drop zone */}
      <div
        onDrop={handleDrop}
        onDragOver={(e) => { e.preventDefault(); setDragOver(true) }}
        onDragLeave={() => setDragOver(false)}
        onClick={() => inputRef.current?.click()}
        className={`
          relative cursor-pointer rounded-lg border-2 border-dashed transition-[background-color,border-color] duration-200
          ${dragOver
            ? 'border-origin-accent bg-origin-accent/10'
            : 'border-white/10 hover:border-white/20 bg-white/[0.02]'
          }
          ${uploading ? 'opacity-60 pointer-events-none' : ''}
        `}
      >
        {preview ? (
          <div className="relative aspect-video overflow-hidden rounded-lg">
            <img src={preview} alt="Preview" className="w-full h-full object-cover" />
            <div className="absolute inset-0 bg-black/60 opacity-0 hover:opacity-100 transition-opacity flex items-center justify-center">
              <span className="text-sm text-white font-medium">Click to replace</span>
            </div>
          </div>
        ) : (
          <div className="flex flex-col items-center justify-center py-8 px-4">
            <svg className="w-10 h-10 text-text-muted mb-3" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M4 16l4.586-4.586a2 2 0 012.828 0L16 16m-2-2l1.586-1.586a2 2 0 012.828 0L20 14m-6-6h.01M6 20h12a2 2 0 002-2V6a2 2 0 00-2-2H6a2 2 0 00-2 2v12a2 2 0 002 2z" />
            </svg>
            <p className="text-sm text-text-muted">Drop image or click to upload</p>
            <p className="text-xs text-text-muted/60 mt-1">Max 5MB · JPG, PNG, WebP</p>
          </div>
        )}

        {uploading && (
          <div className="absolute inset-0 flex items-center justify-center bg-black/40 rounded-lg">
            <div className="animate-spin rounded-full h-6 w-6 border-2 border-t-transparent border-origin-accent" />
          </div>
        )}
      </div>

      <input
        ref={inputRef}
        type="file"
        accept="image/*"
        onChange={handleChange}
        className="hidden"
      />

      {error && (
        <p className="text-xs text-red-400">{error}</p>
      )}
    </div>
  )
}
